import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  uploadShopifyStock,
  uploadManualStock,
  uploadBinAllocation,
  clearUploadResult
} from '../../store/slices/inventorySlice';
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';

const FileUpload: React.FC = () => {
  const dispatch = useAppDispatch();
  const { uploadLoading, uploadError, uploadResult } = useAppSelector((state) => state.inventory);
  const { user } = useAppSelector((state) => state.auth);
  const [selectedFileType, setSelectedFileType] = useState<'shopify_stock' | 'manual_stock' | 'bin_allocation'>('shopify_stock');
  const [dragActive, setDragActive] = useState(false);
  const fileTypes = {
    shopify_stock: {
      label: 'Shopify Stock CSV', 
      description: 'Upload Shopify inventory with stock quantities (creates stock receipts for approval)',
      accepts: '.csv',
      permissions: ['manager', 'executive1', 'executive2']
    },
    manual_stock: {
      label: 'Manual Stock Entry',
      description: 'Upload manual stock entries Excel file',
      accepts: '.xlsx,.xls',
      permissions: ['manager', 'executive2']
    },
    bin_allocation: {
      label: 'Bin Allocation',
      description: 'Upload bin allocation mapping Excel file',
      accepts: '.xlsx,.xls,.csv',
      permissions: ['manager']
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = fileTypes[selectedFileType].accepts.split(',');
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      alert(`Invalid file type. Please upload ${fileTypes[selectedFileType].accepts} files only.`);
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }    try {
      switch (selectedFileType) {
        case 'shopify_stock':
          await dispatch(uploadShopifyStock(file)).unwrap();
          break;
        case 'manual_stock':
          await dispatch(uploadManualStock(file)).unwrap();
          break;
        case 'bin_allocation':
          await dispatch(uploadBinAllocation(file)).unwrap();
          break;
      }
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const canUpload = (fileType: string) => {
    const permissions = fileTypes[fileType as keyof typeof fileTypes].permissions;
    return permissions.includes(user?.role || '');
  };

  return (    <div className="p-6 space-y-6">      <div>
        <h2 className="text-xl font-semibold text-gray-900">File Upload</h2>
        <p className="text-gray-600">Multiple upload methods available - Choose the appropriate method based on your data source</p>      </div>      {/* Available Upload Methods Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Available Upload Methods</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <h4 className="font-medium text-gray-700">Inventory Management</h4>
            <ul className="space-y-1 text-gray-600">
              <li>• Stock CSV (adds inventory quantities)</li>
              <li>• Manual Stock Entry Excel</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium text-gray-700">System Configuration</h4>
            <ul className="space-y-1 text-gray-600">
              <li>• Bin Allocation CSV/Excel</li>
            </ul>
          </div>
        </div>
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> Style catalog uploads are now handled in the Style Management section. 
            Go to Style Management → Upload Styles to import new styles from Shopify.
          </p>
        </div>
      </div>

      {/* File Type Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Select File Type</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {Object.entries(fileTypes).map(([key, config]) => (
            <div
              key={key}
              className={`relative border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                selectedFileType === key
                  ? 'border-blue-500 bg-blue-50'
                  : canUpload(key)
                  ? 'border-gray-200 hover:border-gray-300'
                  : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
              }`}
              onClick={() => canUpload(key) && setSelectedFileType(key as any)}
            >
              <div className="flex items-start">
                <FileText className="h-6 w-6 text-gray-400 mt-1" />
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-medium text-gray-900">{config.label}</h4>
                  <p className="text-xs text-gray-500 mt-1">{config.description}</p>
                  <p className="text-xs text-gray-400 mt-1">Accepts: {config.accepts}</p>
                </div>
              </div>
              
              {!canUpload(key) && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-75 rounded-lg">
                  <span className="text-xs text-gray-500 font-medium">No Permission</span>
                </div>
              )}
              
              {selectedFileType === key && (
                <div className="absolute top-2 right-2">
                  <CheckCircle className="h-5 w-5 text-blue-500" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Upload Area */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Upload File</h3>
        
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-50'
              : uploadLoading
              ? 'border-gray-300 bg-gray-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {uploadLoading ? (
            <div className="space-y-4">
              <Loader2 className="h-12 w-12 animate-spin mx-auto text-blue-600" />
              <div>
                <p className="text-lg font-medium text-gray-900">Uploading File...</p>
                <p className="text-sm text-gray-600">Please wait while we process your file</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Upload className="h-12 w-12 mx-auto text-gray-400" />
              <div>
                <p className="text-lg font-medium text-gray-900">
                  Drop your {fileTypes[selectedFileType].label.toLowerCase()} file here
                </p>
                <p className="text-sm text-gray-600">or click to browse</p>
              </div>
              
              <input
                type="file"
                accept={fileTypes[selectedFileType].accepts}
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                disabled={uploadLoading}
              />
              
              <label
                htmlFor="file-upload"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 cursor-pointer disabled:opacity-50"
              >
                Choose File
              </label>
              
              <p className="text-xs text-gray-500">
                Maximum file size: 10MB. Accepted formats: {fileTypes[selectedFileType].accepts}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Result */}
      {uploadResult && (
        <div className={`rounded-lg p-4 ${uploadResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-start">
            {uploadResult.success ? (
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
            ) : (
              <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
            )}
            
            <div className="ml-3 flex-1">
              <h3 className={`text-sm font-medium ${uploadResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {uploadResult.success ? 'Upload Successful' : 'Upload Failed'}
              </h3>
              
              <p className={`text-sm mt-1 ${uploadResult.success ? 'text-green-700' : 'text-red-700'}`}>
                {uploadResult.message}
              </p>
                {uploadResult.success && (                <div className="mt-3 space-y-1 text-sm text-green-700">
                  <p>• Processed records: {uploadResult.processed_records}</p>
                  {uploadResult.new_styles && <p>• New styles created: {uploadResult.new_styles}</p>}
                  {uploadResult.existing_styles && <p>• Existing styles: {uploadResult.existing_styles}</p>}
                  {uploadResult.new_variants && <p>• New variants created: {uploadResult.new_variants}</p>}
                  {uploadResult.updated_inventory && <p>• Inventory updated: {uploadResult.updated_inventory}</p>}
                  {uploadResult.skipped_gift_vouchers && <p>• Skipped gift vouchers: {uploadResult.skipped_gift_vouchers}</p>}
                  {uploadResult.pending_approval && <p>• Pending approval: {uploadResult.pending_approval}</p>}
                  {uploadResult.auto_approved && <p>• Auto approved: {uploadResult.auto_approved}</p>}
                </div>
              )}
              
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-sm font-medium text-red-800">Errors:</h4>
                  <div className="mt-1 max-h-32 overflow-y-auto">
                    {uploadResult.errors.slice(0, 5).map((error, index) => (
                      <p key={index} className="text-xs text-red-700">
                        Row {error.row}: {error.error}
                      </p>
                    ))}
                    {uploadResult.errors.length > 5 && (
                      <p className="text-xs text-red-700">
                        ... and {uploadResult.errors.length - 5} more errors
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <button
              onClick={() => dispatch(clearUploadResult())}
              className="ml-3 text-gray-400 hover:text-gray-500"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Upload Error */}
      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Upload Error</h3>
              <p className="text-sm text-red-700 mt-1">{uploadError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="h-5 w-5 text-blue-500" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Upload Instructions</h3>            <div className="text-sm text-blue-700 mt-1 space-y-1">
              {selectedFileType === 'shopify_stock' && (
                <>
                  <p>• Upload Shopify inventory CSV with stock quantities after styles are created</p>
                  <p>• Same file format as styles catalog but with quantity data</p>
                  <p>• <strong>Creates stock receipts requiring Executive 2 approval</strong></p>
                  <p>• Executive 2 will approve each item with optimal location allocation</p>
                  <p>• Gift vouchers and gift cards will be automatically skipped</p>
                  <p>• <strong>Note:</strong> Upload styles first in Style Management section before adding stock</p>
                </>
              )}
              
              {selectedFileType === 'manual_stock' && (
                <>
                  <p>• Use Excel format with columns: DATE, ITEM CODE, COLOR, SIZE, QTY, GATEPASS NUMBER</p>
                  <p>• Date format: MM/DD/YYYY or DD/MM/YYYY</p>
                  <p>• Unknown styles will be marked for approval</p>
                </>
              )}
              
              {selectedFileType === 'bin_allocation' && (
                <>
                  <p>• Use Excel or CSV format with columns: Bin Number, Style Code, Quantity, Last Updated</p>
                  <p>• Bin numbers should follow format: A-1-1, B-2-3, etc.</p>
                  <p>• This will update bin allocations and stock locations</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
